import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Wifi, WifiOff, User, Save, RefreshCcw, Trash2, Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { auth, signOut } from "../services/firebaseApi";
import { useAuth } from "../context/AuthContext";
import { usePopup } from "../context/PopupContext";
import { logError } from "../utils/logger";
import { useUserRole } from "../hooks/useUserRole";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import {
  renameUserDevice,
  saveDeviceWifi,
  switchDeviceWifi,
  removeDeviceWifi,
  subscribeDeviceConfig,
  subscribeDeviceLive,
  subscribeUserDevices,
  parseWifiScanNetworks,
  requestDeviceWifiScan,
  resolveDeviceOnline,
} from "../services/deviceService";

const SCAN_TIMEOUT_MS = 25000;
const BOOTSTRAP_WIFI_SSIDS = ["Aryan's S24"];

function isReservedWifiSsid(ssid) {
  const value = String(ssid || "").trim();
  if (!value) return true;
  if (BOOTSTRAP_WIFI_SSIDS.includes(value)) return true;
  if (/^smartfarm-/i.test(value)) return true;
  return false;
}

function rssiToBars(rssi) {
  if (rssi === null || rssi === undefined || rssi === "") return null;
  const n = Number(rssi);
  if (!Number.isFinite(n)) return null;
  if (n >= -50) return 4;
  if (n >= -60) return 3;
  if (n >= -70) return 2;
  if (n >= -80) return 1;
  return 0;
}

function SignalBars({ rssi }) {
  const bars = rssiToBars(rssi);
  if (bars === null) return null;
  return (
    <span className="inline-flex items-end gap-0.5 h-4" aria-hidden>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`w-1 rounded-sm ${i <= bars ? "bg-accent" : "bg-border"}`}
          style={{ height: `${i * 3 + 4}px` }}
        />
      ))}
    </span>
  );
}

export default function SettingsPage() {
  const [userDevices, setUserDevices] = useState({});
  const [selectedDeviceID, setSelectedDeviceID] = useState("");
  const [newDeviceName, setNewDeviceName] = useState("");
  const [savedNetworks, setSavedNetworks] = useState([]);
  const [selectedSsid, setSelectedSsid] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [switchingSsid, setSwitchingSsid] = useState("");
  const [removingSsid, setRemovingSsid] = useState("");
  const [wifiNetworks, setWifiNetworks] = useState([]);
  const [wifiScanAt, setWifiScanAt] = useState(0);
  const [scanPhase, setScanPhase] = useState("idle");
  const [scanRequestedAt, setScanRequestedAt] = useState(0);
  const [liveData, setLiveData] = useState({});
  const [statusTick, setStatusTick] = useState(0);
  const [editorMode, setEditorMode] = useState("none");
  const [tab, setTab] = useState("profile");
  const { user } = useAuth();
  const { effectiveRole } = useUserRole();
  const { showPopup } = usePopup();
  const canUpdateDevice = effectiveRole === "owner" || effectiveRole === "operator" || effectiveRole === "admin" || effectiveRole === "super_admin";

  const selectedDevice = userDevices[selectedDeviceID] || {};
  const isSimulator = !!selectedDevice.isSimulator;
  const deviceOnline = resolveDeviceOnline(liveData, isSimulator);
  const connectedSsid = String(
    liveData?.meta?.wifiSSID || liveData?.deviceStatus?.wifiSSID || ""
  ).trim();

  const nearbyRows = useMemo(() => {
    if (!deviceOnline) return [];
    return wifiNetworks
      .filter((n) => n.ssid && !isReservedWifiSsid(n.ssid))
      .map((n) => {
        const saved = savedNetworks.find((entry) => entry.ssid === n.ssid);
        return {
          ssid: n.ssid,
          password: saved?.password || "",
          rssi: n.rssi,
          saved: !!saved,
          inRange: true,
          connected: connectedSsid === n.ssid,
        };
      });
  }, [deviceOnline, wifiNetworks, savedNetworks, connectedSsid]);

  const savedRows = useMemo(() => {
    return savedNetworks
      .filter((entry) => !isReservedWifiSsid(entry.ssid))
      .map((entry) => ({
        ssid: entry.ssid,
        password: entry.password || "",
        rssi: nearbyRows.find((n) => n.ssid === entry.ssid)?.rssi ?? null,
        saved: true,
        inRange: nearbyRows.some((n) => n.ssid === entry.ssid),
        connected: deviceOnline && connectedSsid === entry.ssid,
      }));
  }, [savedNetworks, nearbyRows, deviceOnline, connectedSsid]);

  const is5GhzHint = /5g|5ghz|_5g/i.test(selectedSsid);
  const isDeviceApSsid = /^smartfarm-/i.test(selectedSsid.trim());
  const showPasswordPanel = editorMode === "new" || editorMode === "manual";
  const autoScanRef = useRef("");
  const currentDeviceName = userDevices[selectedDeviceID]?.name || "Device";

  useEffect(() => {
    const timer = setInterval(() => setStatusTick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeUserDevices(user.uid, (devices) => {
      setUserDevices(devices);
      const ids = Object.keys(devices);
      if (ids.length > 0 && !selectedDeviceID) {
        setSelectedDeviceID(ids[0]);
      }
    });
    return () => unsubscribe();
  }, [selectedDeviceID, user]);

  useEffect(() => {
    if (!selectedDeviceID || !user) return;
    const unsubscribeUserDevices = subscribeUserDevices(user.uid, (devices) => {
      const deviceData = devices?.[selectedDeviceID] || {};
      setNewDeviceName(deviceData?.name || "");
    });

    const unsubscribeConfig = subscribeDeviceConfig(selectedDeviceID, (configData) => {
      let networks = Array.isArray(configData.wifiNetworks) ? configData.wifiNetworks : [];
      if (!networks.length && configData.ssid) {
        networks = [{ ssid: configData.ssid, password: configData.password || "" }];
      }
      setSavedNetworks(networks.filter((entry) => !isReservedWifiSsid(entry.ssid)));
    });

    const unsubscribeLive = subscribeDeviceLive(selectedDeviceID, (live) => {
      const networks = parseWifiScanNetworks(live?.wifiScan || {});
      const scannedAt = Number(live?.wifiScan?.scannedAt || 0);
      const online = resolveDeviceOnline(live, isSimulator);
      setWifiNetworks(online ? networks : []);
      if (scannedAt > 0) setWifiScanAt(scannedAt);
      setLiveData(live);
    });

    return () => {
      unsubscribeUserDevices();
      unsubscribeConfig();
      unsubscribeLive();
    };
  }, [selectedDeviceID, user, userDevices, isSimulator]);

  useEffect(() => {
    if (scanPhase !== "scanning" || !scanRequestedAt) return;

    if (wifiScanAt >= scanRequestedAt && wifiScanAt > 0) {
      setScanPhase("done");
      return;
    }

    const timer = setTimeout(() => {
      setScanPhase((prev) => (prev !== "scanning" ? prev : "timeout"));
    }, SCAN_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [scanPhase, scanRequestedAt, wifiScanAt, wifiNetworks.length, deviceOnline, statusTick]);

  useEffect(() => {
    setScanPhase("idle");
    setScanRequestedAt(0);
    setWifiNetworks([]);
    setWifiScanAt(0);
    setEditorMode("none");
    setSelectedSsid("");
    setEditPassword("");
    setSavedNetworks([]);
    autoScanRef.current = "";
  }, [selectedDeviceID]);

  const handleSaveDeviceName = async () => {
    if (!newDeviceName.trim()) {
      showPopup({ type: "warning", title: "Invalid Name", message: "Device name cannot be empty." });
      return;
    }
    if (!canUpdateDevice) {
      showPopup({ type: "warning", title: "Read-only role", message: "Viewer role can view settings but cannot update device data." });
      return;
    }
    try {
      await renameUserDevice(user.uid, selectedDeviceID, newDeviceName);
      showPopup({ type: "success", title: "Settings Saved", message: "Device name updated successfully." });
    } catch (error) {
      logError("SettingsPage", "Failed to save device name", error, { uid: user?.uid || null, deviceId: selectedDeviceID });
      showPopup({ type: "error", title: "Save Failed", message: "Unable to save device name right now." });
    }
  };

  const handleScanWifi = useCallback(async () => {
    if (!canUpdateDevice) {
      showPopup({ type: "warning", title: "Read-only role", message: "Viewer role cannot request a Wi-Fi scan." });
      return;
    }
    if (isSimulator) {
      showPopup({ type: "warning", title: "Simulator device", message: "Wi-Fi scan requires a physical device." });
      return;
    }
    if (!deviceOnline) {
      toast.warning("Device offline", { description: "Nearby scan runs on the board. It needs to be online first." });
      return;
    }
    setEditorMode("none");
    setScanPhase("scanning");
    setScanRequestedAt(Math.floor(Date.now() / 1000));
    try {
      await requestDeviceWifiScan(selectedDeviceID);
    } catch (error) {
      logError("SettingsPage", "Failed to request wifi scan", error, { uid: user?.uid || null, deviceId: selectedDeviceID });
      setScanPhase("error");
      toast.error("Scan failed");
    }
  }, [canUpdateDevice, deviceOnline, isSimulator, selectedDeviceID, showPopup, user?.uid]);

  useEffect(() => {
    if (tab !== "wifi" || !deviceOnline || isSimulator || !selectedDeviceID) return;
    const key = `${selectedDeviceID}:online`;
    if (autoScanRef.current === key) return;
    autoScanRef.current = key;
    handleScanWifi();
  }, [tab, deviceOnline, isSimulator, selectedDeviceID, handleScanWifi]);

  const handleNetworkPick = async (row) => {
    if (!row?.ssid) return;

    if (row.connected) return;

    if (row.saved) {
      setEditorMode("none");
      setSelectedSsid("");
      setEditPassword("");
      if (isReservedWifiSsid(row.ssid)) {
        toast.warning("Setup network only", { description: "This network is for device setup and cannot be selected here." });
        return;
      }
      if (!row.inRange) {
        toast.message(`${row.ssid} saved`, {
          description: "The device will join this network automatically when it is in range.",
        });
        return;
      }
      const savedPass = row.password || savedNetworks.find((n) => n.ssid === row.ssid)?.password || "";
      if (!savedPass || !canUpdateDevice || isSimulator) return;
      if (!deviceOnline) {
        toast.warning("Device offline", { description: "Power the board on a 2.4 GHz network first." });
        return;
      }
      if (connectedSsid) {
        toast.message(`Staying on ${connectedSsid}`, {
          description: "The board will try another saved network only after this one goes offline or is out of range.",
        });
        return;
      }

      setSwitchingSsid(row.ssid);
      try {
        await switchDeviceWifi(selectedDeviceID, row.ssid, savedPass, user.uid);
        toast.success(`Switching to ${row.ssid}`, { description: "Device will reconnect within ~15 seconds." });
      } catch (error) {
        logError("SettingsPage", "Failed to switch wifi", error, { uid: user?.uid || null, deviceId: selectedDeviceID });
        toast.error("Could not switch network");
      } finally {
        setSwitchingSsid("");
      }
      return;
    }

    setSelectedSsid(row.ssid);
    setEditPassword("");
    setEditorMode("new");
  };

  const handleSaveWiFi = async () => {
    const ssid = selectedSsid.trim();
    if (!ssid || !editPassword.trim()) {
      showPopup({ type: "warning", title: "Missing Credentials", message: "SSID and password are both required." });
      return;
    }
    if (isDeviceApSsid || isReservedWifiSsid(ssid)) {
      showPopup({
        type: "warning",
        title: "Wrong network name",
        message: isDeviceApSsid
          ? "SmartFarm-003 is the board setup Wi-Fi, not your home router."
          : "This network is reserved for device setup only.",
      });
      return;
    }
    if (!canUpdateDevice) {
      showPopup({ type: "warning", title: "Read-only role", message: "Viewer role cannot update Wi-Fi." });
      return;
    }
    try {
      await saveDeviceWifi(selectedDeviceID, ssid, editPassword, user.uid);
      toast.success("Network saved", {
        description: deviceOnline
          ? "Device will connect shortly and remember this network for next time."
          : "Saved. If the device is at a new place, join its setup Wi-Fi to pick this network from the live list.",
      });
      setEditorMode("none");
      setSelectedSsid("");
      setEditPassword("");
    } catch (error) {
      logError("SettingsPage", "Failed to update wifi credentials", error, { uid: user?.uid || null, deviceId: selectedDeviceID });
      showPopup({ type: "error", title: "Wi-Fi Update Failed", message: "Could not save network right now." });
    }
  };

  const handleForgetWifi = async (event, ssid) => {
    event.preventDefault();
    event.stopPropagation();
    const network = String(ssid || "").trim();
    if (!network || !canUpdateDevice) return;
    setRemovingSsid(network);
    try {
      await removeDeviceWifi(selectedDeviceID, network, user.uid);
      setSavedNetworks((prev) => prev.filter((entry) => entry.ssid !== network));
      toast.success(`Removed ${network}`);
    } catch (error) {
      logError("SettingsPage", "Failed to remove wifi", error, { uid: user?.uid || null, deviceId: selectedDeviceID });
      toast.error("Could not remove network");
    } finally {
      setRemovingSsid("");
    }
  };

  const handleClearWifiForm = () => {
    setSelectedSsid("");
    setEditPassword("");
    setEditorMode("none");
    setScanPhase("idle");
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch {
      window.location.href = "/login";
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Manage your device settings"
        title="Settings for"
        highlight={currentDeviceName}
        description="Customize device names and Wi-Fi credentials. Keep your sensors running smoothly."
      />

      <PageSection variant="content">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-3xl bg-card border border-border p-6 md:p-8 mb-6 shadow-soft">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Active device</label>
            <div className="relative mt-2">
              <select value={selectedDeviceID} onChange={(e) => setSelectedDeviceID(e.target.value)} className="w-full h-12 px-5 pr-12 rounded-full bg-secondary/50 border border-border appearance-none text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                {Object.keys(userDevices).map((id) => (
                  <option key={id} value={id}>
                    {userDevices[id]?.name || id}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
            </div>
          </div>

          <div className="flex gap-1 p-1 rounded-full bg-secondary/60 w-full sm:w-fit mb-6 flex-wrap">
            {[{ k: "profile", l: "Device Profile" }, { k: "wifi", l: "Wi-Fi" }, { k: "danger", l: "Danger Zone" }].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} className={`px-5 py-2 rounded-full text-sm transition-all ${tab === t.k ? "bg-card shadow-sm" : "text-muted-foreground"}`} type="button">
                {t.l}
              </button>
            ))}
          </div>

          {tab === "profile" && (
            <div className="rounded-3xl bg-card border border-border p-8 shadow-soft">
              <div className="flex items-center gap-3 mb-2"><User className="h-5 w-5 text-accent" /><h3 className="font-display text-2xl">Device profile</h3></div>
              <p className="text-sm text-muted-foreground mb-6">Update how this device appears in dashboard and reports.</p>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Device name</label>
              <input value={newDeviceName} onChange={(e) => setNewDeviceName(e.target.value)} className="mt-2 w-full h-12 px-5 rounded-full bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent" />
              <Button variant="accent" className="mt-6" onClick={handleSaveDeviceName} disabled={!canUpdateDevice}><Save className="h-4 w-4" /> Save device name</Button>
            </div>
          )}

          {tab === "wifi" && (
            <div className="rounded-3xl bg-card border border-border p-4 sm:p-6 md:p-8 shadow-soft">
              <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wifi className="h-5 w-5 text-accent shrink-0" />
                    <h3 className="font-display text-2xl whitespace-nowrap">Wi-Fi</h3>
                    {!isSimulator && (
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shrink-0 ${deviceOnline ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                        {deviceOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                        {deviceOnline ? "Online" : "Offline"}
                      </span>
                    )}
                  </div>
                  {!isSimulator && deviceOnline && (
                    <p className="text-sm text-muted-foreground mt-2 sm:pl-8 break-words">
                      {connectedSsid ? `Connected to ${connectedSsid}` : "Online — waiting for network name"}
                    </p>
                  )}
                </div>
                {!isSimulator && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto shrink-0"
                    onClick={handleScanWifi}
                    disabled={!canUpdateDevice || !deviceOnline || scanPhase === "scanning"}
                  >
                    <RefreshCcw className={`h-4 w-4 ${scanPhase === "scanning" ? "animate-spin" : ""}`} />
                    {scanPhase === "scanning" ? "Scanning…" : "Scan nearby"}
                  </Button>
                )}
              </div>

              {!isSimulator && (
                <>
                  <div className="rounded-2xl border border-border overflow-hidden mb-4">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/40 border-b border-border">
                      <span className="text-sm font-medium text-foreground">Available nearby</span>
                      {scanPhase === "scanning" && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Loader2 className="h-3 w-3 animate-spin" /> Scanning…
                        </span>
                      )}
                    </div>
                    {nearbyRows.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-muted-foreground">
                        {deviceOnline
                          ? scanPhase === "scanning"
                            ? "Looking for networks the board can see…"
                            : "Tap Scan nearby to list 2.4 GHz networks around the device."
                          : "Nearby list appears after the device is online."}
                      </div>
                    ) : (
                      <ul className="divide-y divide-border max-h-72 overflow-y-auto">
                        {nearbyRows.map((n) => {
                          const subtitle = n.connected
                            ? "Connected"
                            : switchingSsid === n.ssid
                              ? "Connecting…"
                              : n.saved
                                ? "Saved — tap to connect"
                                : "Tap to join";
                          return (
                            <li key={`near-${n.ssid}`}>
                              <button
                                type="button"
                                onClick={() => handleNetworkPick(n)}
                                disabled={switchingSsid === n.ssid}
                                className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-secondary/50 ${selectedSsid === n.ssid ? "bg-accent/5" : ""} ${n.connected ? "bg-accent/5" : ""}`}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{n.ssid}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {n.connected && <Check className="h-4 w-4 text-accent" />}
                                  <SignalBars rssi={n.rssi} />
                                  <Lock className="h-4 w-4 text-muted-foreground" />
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={() => { setEditorMode("manual"); setSelectedSsid(""); setEditPassword(""); }}
                      className="w-full px-4 py-3 text-sm text-accent hover:bg-secondary/50 border-t border-border text-left"
                    >
                      Other network…
                    </button>
                  </div>

                  {savedRows.length > 0 && (
                    <div className="rounded-2xl border border-border overflow-hidden mb-6">
                      <div className="px-4 py-2.5 bg-secondary/40 border-b border-border">
                        <span className="text-sm font-medium text-foreground">Saved</span>
                      </div>
                      <ul className="divide-y divide-border">
                        {savedRows.map((n) => (
                          <li key={`saved-${n.ssid}`} className="flex items-stretch">
                            <button
                              type="button"
                              onClick={() => handleNetworkPick(n)}
                              className={`min-w-0 flex-1 flex items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-secondary/50 ${n.connected ? "bg-accent/5" : ""}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{n.ssid}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                  {n.connected
                                    ? "Connected"
                                    : n.inRange
                                      ? "Nearby — tap to connect"
                                      : "Saved — joins when in range"}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {n.connected && <Check className="h-4 w-4 text-accent" />}
                                <SignalBars rssi={n.rssi} />
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={(event) => handleForgetWifi(event, n.ssid)}
                              disabled={!canUpdateDevice || removingSsid === n.ssid}
                              className="px-3 sm:px-4 text-muted-foreground hover:text-destructive hover:bg-destructive/5 border-l border-border shrink-0"
                              title={`Forget ${n.ssid}`}
                            >
                              {removingSsid === n.ssid ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {showPasswordPanel && (
                <div className="space-y-4 mb-6 rounded-2xl border border-border p-4 bg-secondary/20">
                  {(editorMode === "manual" || isSimulator) && (
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground">Network name</label>
                      <input
                        value={selectedSsid}
                        onChange={(e) => setSelectedSsid(e.target.value)}
                        placeholder="Network name"
                        className="mt-2 w-full h-12 px-5 rounded-full bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                  )}
                  {editorMode === "new" && selectedSsid && (
                    <p className="text-sm font-medium">{selectedSsid}</p>
                  )}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Password</label>
                    <input
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Wi-Fi password"
                      className="mt-2 w-full h-12 px-5 rounded-full bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>
              )}

              {is5GhzHint && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-4">
                  2.4 GHz only — this name looks like a 5 GHz network.
                </p>
              )}

              {showPasswordPanel && (
                <div className="flex gap-3 flex-wrap">
                  <Button variant="accent" onClick={handleSaveWiFi} disabled={!canUpdateDevice || !selectedSsid.trim() || !editPassword.trim()}>
                    <Save className="h-4 w-4" /> Save network
                  </Button>
                  <Button variant="outline" onClick={handleClearWifiForm}>
                    Clear
                  </Button>
                </div>
              )}
            </div>
          )}

          {tab === "danger" && (
            <div className="rounded-3xl bg-destructive/5 border border-destructive/30 p-8">
              <div className="flex items-center gap-3 mb-2"><Trash2 className="h-5 w-5 text-destructive" /><h3 className="font-display text-2xl text-destructive">Danger zone</h3></div>
              <p className="text-sm text-muted-foreground mb-6">Permanently remove this device and its history. This cannot be undone.</p>
              <Button variant="destructive"><Trash2 className="h-4 w-4" /> Delete device</Button>
            </div>
          )}

          <div className="mt-6 rounded-3xl bg-card border border-border p-8 shadow-soft">
            <h3 className="font-display text-2xl">Account profile</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Manage your personal profile information used across the platform.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/profile"><Button variant="outline">Open profile page</Button></Link>
              <Button variant="destructive" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
}

